import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { Button } from './button';

const InputGroup = ({ className, ...props }: React.ComponentProps<'div'>): React.JSX.Element => {
  return (
    <div className={cn(
      'group/input-group border-input bg-white shadow-xs relative flex w-full items-center rounded-sm border ' +
        'ring-2 ring-offset-0 ring-transparent transition-[color,border-color,box-shadow]',
      'h-9 has-[>textarea]:h-auto',

      // Variants based on alignment.
      'has-[>[data-align=inline-start]]:[&>input]:pl-2',
      'has-[>[data-align=inline-end]]:[&>input]:pr-2',
      'has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col ' +
        'has-[>[data-align=block-start]]:[&>input]:pb-3',
      'has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col ' +
        'has-[>[data-align=block-end]]:[&>input]:pt-3',

      // Focus state.
      'focus-within:!ring-black focus-within:!bg-white focus-within:border-black',

      // Error state.
      'has-[[data-slot][aria-invalid=true]]:border-primary ' +
        'has-[[data-slot][aria-invalid=true]]:ring-primary ' +
        'has-[[data-slot][aria-invalid=true]]:bg-[var(--input-error-background)]',

      className
    )}
    data-slot="input-group"
    role="group"
    {...props}
    />
  );
};

const inputGroupAddonVariants = cva(
  'text-muted-foreground flex h-auto cursor-text select-none items-center justify-center gap-2 py-1.5 text-sm font-medium ' +
    'group-data-[disabled=true]/input-group:opacity-50 [&>kbd]:rounded-[calc(var(--radius)-5px)] ' +
    "[&>svg:not([class*='size-'])]:size-4",
  {
    defaultVariants: {
      align: 'inline-start',
    },
    variants: {
      align: {
        'block-end':
          '[.border-t]:pt-3 order-last w-full justify-start px-3 pb-3 group-has-[>input]/input-group:pb-2.5',
        'block-start':
          '[.border-b]:pb-3 order-first w-full justify-start px-3 pt-3 group-has-[>input]/input-group:pt-2.5',
        'inline-end':
          'order-last pr-3 has-[>kbd]:mr-[-0.35rem]',
        'inline-start':
          'order-first pl-3 has-[>kbd]:ml-[-0.35rem]',
      },
    },
  }
);

const InputGroupAddon = ({
  align = 'inline-start',
  className,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof inputGroupAddonVariants>): React.JSX.Element => {
  return (
    <div className={cn(inputGroupAddonVariants({ align }), className)}
      data-align={align}
      data-slot="input-group-addon"
      role="group"
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) {
          return;
        }
        e.currentTarget.parentElement?.querySelector('input')?.focus();
      }}
      {...props}
    />
  );
};

const inputGroupButtonVariants = cva(
  'flex items-center gap-2 text-sm shadow-none',
  {
    defaultVariants: {
      size: 'xs',
    },
    variants: {
      size: {
        'icon-sm': 'size-8 p-0 has-[>svg]:p-0',
        'icon-xs':
          'size-6 rounded-[calc(var(--radius)-5px)] p-0 has-[>svg]:p-0',
        sm: 'h-8 gap-1.5 rounded-md px-2.5 has-[>svg]:px-2.5',
        xs: 'h-6 gap-1 rounded-[calc(var(--radius)-5px)] px-2 has-[>svg]:px-2 ' +
          "[&>svg:not([class*='size-'])]:size-3.5",
      },
    },
  }
);

const InputGroupButton = ({
  className,
  size = 'xs',
  type = 'button',
  variant = 'ghost',
  ...props
}: Omit<React.ComponentProps<typeof Button>, 'size'> &
  VariantProps<typeof inputGroupButtonVariants>): React.JSX.Element => {
  return (
    <Button className={cn(inputGroupButtonVariants({ size }), className)}
      data-size={size}
      data-test="input-group-button"
      type={type}
      variant={variant}
      {...props}
    />
  );
};

const InputGroupText = ({ className, ...props }: React.ComponentProps<'span'>): React.JSX.Element => {
  return (
    <span className={cn(
      'text-muted-foreground flex items-center gap-2 text-sm ' +
        "[&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none",
      className
    )}
    {...props}
    />
  );
};

const InputGroupInput = ({
  className,
  ...props
}: React.ComponentProps<'input'>): React.JSX.Element => {
  return (
    <input className={cn(
      'file:text-foreground placeholder:text-muted-foreground flex-1 min-w-0 bg-transparent px-3 py-2 text-base ' +
        'outline-none focus-visible:outline-none',
      className
    )}
    data-slot="input-group-control"
    {...props}
    />
  );
};

const InputGroupTextarea = ({
  className,
  ...props
}: React.ComponentProps<'textarea'>): React.JSX.Element => {
  return (
    <Textarea className={cn(
      'flex-1 resize-none rounded-none border-0 bg-transparent py-3 shadow-none focus-visible:ring-0 ' +
        'dark:bg-transparent',
      className
    )}
    data-slot="input-group-control"
    {...props}
    />
  );
};

export {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  InputGroupText,
  InputGroupTextarea,
};
